// src/services/api.ts
import axios, { InternalAxiosRequestConfig } from 'axios';
import { supabase } from '../lib/supabaseClient.ts';

// v1 base URL (existing)
const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.autotuneresume.com/api/v1';
console.log(`BASE_URL (v1): ${BASE_URL}`);

// Export API_URL for use in other modules (e.g., SSE)
export const API_URL = BASE_URL;


// Create axios instances with default config
const commonConfig = {
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false, // No cookies; we use Bearer JWT only
  timeout: 30000, // 30 second timeout to prevent hanging requests
};

const api = axios.create({ baseURL: BASE_URL, ...commonConfig });

// Helper: parse filename from Content-Disposition header (handles RFC 5987 and quoted/unquoted forms)
function parseFilenameFromContentDisposition(cd: string | undefined, fallback: string): string {
  if (!cd) return fallback;
  try {
    // Prefer RFC 5987 form: filename*=UTF-8''encoded-name.pdf
    const rfcMatch = cd.match(/filename\*\s*=\s*([^;]+)/i);
    if (rfcMatch) {
      let v = rfcMatch[1].trim();
      // Strip surrounding quotes if present
      v = v.replace(/^\"|\"$/g, '');
      // If it contains charset''value, split and decode value part
      const parts = v.split("''");
      if (parts.length === 2) {
        const encoded = parts[1];
        try { return decodeURIComponent(encoded); } catch { return encoded; }
      }
      // Otherwise, try decode whole token after potential UTF-8'' prefix
      v = v.replace(/^UTF-8''/i, '');
      try { return decodeURIComponent(v); } catch { return v; }
    }

    // Standard quoted: filename="name.pdf"
    const quoted = cd.match(/filename\s*=\s*\"([^\"]+)\"/i);
    if (quoted) return quoted[1];

    // Unquoted token: filename=name.pdf
    const unquoted = cd.match(/filename\s*=\s*([^;]+)/i);
    if (unquoted) return unquoted[1].trim();
  } catch {
    // ignore
  }
  return fallback;
}

// Helper to attach JWT from Supabase or fallback to backend token
async function attachToken(config: InternalAxiosRequestConfig) {
  const { data } = await supabase.auth.getSession();
  const sbToken = data.session?.access_token;
  const headers = (config.headers = config.headers || {});
  if (sbToken) {
    headers.Authorization = `Bearer ${sbToken}`;
    return config;
  }
  // Fallback: backend-issued token from Email/Password login
  try {
    const ls = typeof window !== 'undefined' ? window.localStorage : undefined;
    const backendToken = ls?.getItem('authToken');
    const backendTokenType = ls?.getItem('authTokenType') || 'Bearer';
    if (backendToken) {
      headers.Authorization = `${backendTokenType} ${backendToken}`;
    }
  } catch (e) {
    // ignore storage errors
  }
  return config;
}

api.interceptors.request.use(attachToken);

// Error interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get 401 Unauthorized, the token is invalid - clear it
    if (error.response?.status === 401) {
      console.log('🧹 401 error detected - clearing invalid tokens and redirecting to login');
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('authToken');
          localStorage.removeItem('authTokenType');
          // Also clear any potential Supabase tokens
          const sbKeys = [
            'sb-access-token',
            'sb-' + (process.env.REACT_APP_SUPABASE_URL?.replace('https://', '').replace('.supabase.co', '') || 'local') + '-auth-token'
          ];
          sbKeys.forEach(key => localStorage.removeItem(key));

          // Force redirect to login page after clearing tokens
          setTimeout(() => {
            if (window.location.pathname.startsWith('/app')) {
              console.log('🔄 Redirecting to login after token cleanup');
              window.location.href = '/login';
            }
          }, 100);
        }
      } catch (e) {
        // Ignore storage errors
      }
    }

    // If we get 402 Payment Required (Insufficient Balance), emit custom event
    if (error.response?.status === 402) {
      console.log('💰 402 Insufficient Balance error detected');
      // FastAPI wraps errors in { detail: {...} }, so check both structures
      const balanceError = error.response.data?.detail || error.response.data;
      console.log('💰 Balance error data:', balanceError);
      // Check for insufficient_credits error code
      if (balanceError?.error === 'insufficient_credits') {
        console.log('💰 Dispatching insufficient-balance event with:', balanceError);
        // Emit custom event for components to handle
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('insufficient-balance', {
            detail: balanceError
          }));
          console.log('💰 Event dispatched successfully');
        } else {
          console.warn('💰 Window not available, cannot dispatch event');
        }
      } else {
        console.warn('💰 Balance error does not match insufficient_credits:', balanceError?.error);
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to calculate job match percentage based on job characteristics
const calculateJobMatch = (job: any, resumeId?: string): number => {
  let matchScore = 0;
  let totalFactors = 0;

  // Factor 1: Job recency (more recent = higher match)
  if (job.date_posted) {
    const postedDate = new Date(job.date_posted);
    const daysSincePosted = Math.floor((Date.now() - postedDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSincePosted <= 7) matchScore += 25; // Very recent
    else if (daysSincePosted <= 30) matchScore += 20; // Recent
    else if (daysSincePosted <= 90) matchScore += 15; // Moderately recent
    else matchScore += 10; // Older posts

    totalFactors += 25;
  }

  // Factor 2: Job description completeness and quality
  if (job.description) {
    const descLength = job.description.length;
    if (descLength > 500) matchScore += 20; // Detailed description
    else if (descLength > 200) matchScore += 15; // Good description
    else if (descLength > 50) matchScore += 10; // Basic description
    else matchScore += 5; // Poor description

    totalFactors += 20;
  }

  // Factor 3: Salary information availability
  if (job.min_amount || job.max_amount) {
    matchScore += 15; // Has salary info
  } else {
    matchScore += 5; // No salary info
  }
  totalFactors += 15;

  // Factor 4: Remote work availability
  if (job.is_remote === true) {
    matchScore += 15; // Remote work available
  } else {
    matchScore += 10; // On-site only
  }
  totalFactors += 15;

  // Factor 5: Job source credibility
  const credibleSites = ['linkedin', 'indeed', 'glassdoor', 'monster', 'ziprecruiter'];
  if (job.site && credibleSites.some(site => job.site.toLowerCase().includes(site))) {
    matchScore += 15; // Credible job site
  } else {
    matchScore += 10; // Other job sites
  }
  totalFactors += 15;

  // Factor 6: Job type preference (Full-time generally preferred)
  if (job.job_type === 'Full-time') {
    matchScore += 10;
  } else if (job.job_type === 'Contract' || job.job_type === 'Part-time') {
    matchScore += 7;
  } else {
    matchScore += 5;
  }
  totalFactors += 10;

  // Calculate percentage and add some variance for realism
  let percentage = Math.round((matchScore / totalFactors) * 100);

  // Add consistent but varied adjustment based on job ID for realism
  if (job.id) {
    const idHash = job.id.toString().split('').reduce((a: number, b: string) => a + b.charCodeAt(0), 0);
    const adjustment = (idHash % 10) - 5; // -5 to +4 adjustment
    percentage = Math.max(45, Math.min(98, percentage + adjustment));
  }

  return percentage;
};

// Helper function to build query parameters
const buildParams = (options: {
  page?: number;
  size?: number;
  search?: string;
  filter?: Record<string, any>;
}) => {
  const { page = 1, size = 10, search, filter } = options;
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });

  if (search) {
    params.append('search', search);
  }

  // Handle filter parameters
  if (filter) {
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(`filter[${key}]`, value.toString());
      }
    });
  }

  return params;
};

// Auth API
export const auth = {
  login: async (email: string, password: string, timeout: number = 50000) => {
    try {
      const response = await api.post('/login',
        { email, password },
        {
          timeout,
          timeoutErrorMessage: 'Server is taking longer than expected to respond'
        }
      );
      const data = response.data;
      // Persist backend token if present
      try {
        const token = data?.token || data?.access_token;
        const tokenType = data?.token_type || data?.type || 'Bearer';
        if (token && typeof window !== 'undefined') {
          window.localStorage.setItem('authToken', token);
          window.localStorage.setItem('authTokenType', tokenType);
        }
      } catch { /* ignore localStorage errors */ }
      return data;
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Server is still starting up, please wait');
      }
      // Add specific handling for other common errors
      if (!error.response) {
        throw new Error('Unable to reach server. Please check your connection.');
      }
      throw error;
    }
  },
  signup: async (name: string, email: string, password: string) => {
    const response = await api.post('/signup', { name, email, password });
    return response.data;
  },
  isAuthenticated: () => {
    try {
      if (typeof window === 'undefined') return false;

      // Check various possible Supabase token storage keys
      const sbKeys = [
        'sb-access-token',
        'sb-' + (process.env.REACT_APP_SUPABASE_URL?.replace('https://', '').replace('.supabase.co', '') || 'local') + '-auth-token'
      ];

      const hasSb = sbKeys.some(key => !!localStorage.getItem(key));
      const backendToken = localStorage.getItem('authToken');

      // Accept both Supabase sessions and backend tokens
      return hasSb || !!backendToken;
    } catch (e) {
      return false;
    }
  },
  logout: async () => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authTokenType');
      }
    } catch { /* ignore */ }
  }
};

// User API
export const user = {
  getProfile: async () => {
    const response = await api.get('/me');
    return response.data;
  },
  updateProfile: async (data: { name?: string; linkedin_url?: string; }) => {
    const response = await api.patch('/me', data);
    return response.data;
  },
  listResumes: async () => {
    const response = await api.get('/me/resumes');
    return response.data;
  },
  getResumeAnalysis: async (resumeId: string) => {
    if (!resumeId) {
      throw new Error("A resume ID is required to fetch analysis.");
    }
    const response = await api.get(`/me/resumes/${resumeId}/analysis`);
    return response.data;
  },
  uploadResume: async (formData: FormData) => {
    const response = await api.post('/me/resume', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getUnreadNotificationCount: async () => {
    // Use the lightweight count endpoint - just returns count, no notification data
    const response = await api.get('/notifications/count');
    return response.data.count || 0;
  },
  getIntegrations: async () => {
    const response = await api.get('/me/integrations');
    return response.data;
  },
  deleteResume: async (resumeId: string) => {
    const response = await api.delete(`/me/resumes/${resumeId}`);
    return response.data;
  },
  setDefaultResume: async (resumeId: string) => {
    const response = await api.patch(`/me/resumes/${resumeId}/set-default`);
    return response.data;
  },
  retryResumeAnalysis: async (resumeId: string) => {
    const response = await api.post(`/me/resumes/${resumeId}/retry`);
    return response.data;
  },
  shareResume: async (resumeId: string, title?: string | null, accessControl?: string | null) => {
    const payload: any = {
      title: title,
      access_control: accessControl
    };
    const response = await api.post(`/me/resumes/${resumeId}/share`, payload);
    return response.data;
  },
  /**
   * Download the original uploaded resume PDF.
   * Returns a Blob and best-effort filename parsed from Content-Disposition.
   */
  downloadOriginalResume: async (
    resumeId: string,
    options?: { fallbackFilename?: string }
  ): Promise<{ blob: Blob; filename: string }> => {
    if (!resumeId) throw new Error('resumeId is required');
    const response = await api.get(`/me/resumes/${resumeId}/download`, {
      responseType: 'blob',
    });
    // Try to parse filename from headers
    const disposition: string | undefined = (response.headers as any)['content-disposition'] || (response.headers as any)['Content-Disposition'];
    const filename = parseFilenameFromContentDisposition(disposition, options?.fallbackFilename || 'resume.pdf');
    return { blob: response.data, filename };
  },
  /**
   * Download the generated templated resume PDF if available.
   */
  downloadTemplatedResume: async (
    resumeId: string,
    options?: { timeout?: number; fallbackFilename?: string }
  ): Promise<{ blob: Blob; filename: string }> => {
    if (!resumeId) throw new Error('resumeId is required');

    // Step 1: trigger generation (two-step process per backend contract)
    await api.post(
      `/me/resumes/${resumeId}/generate-template`,
      {},
      { timeout: options?.timeout ?? 60_000 }
    );

    // Step 2: download the generated PDF
    const response = await api.get(`/me/resumes/${resumeId}/download-template`, {
      responseType: 'blob',
    });
    const disposition: string | undefined = (response.headers as any)['content-disposition'] || (response.headers as any)['Content-Disposition'];
    const filename = parseFilenameFromContentDisposition(disposition, options?.fallbackFilename || 'templated-resume.pdf');
    return { blob: response.data, filename };
  },

  /** Trigger templated resume generation only (no download). */
  generateTemplatedResume: async (
    resumeId: string,
    options?: { timeout?: number }
  ): Promise<any> => {
    if (!resumeId) throw new Error('resumeId is required');
    const response = await api.post(
      `/me/resumes/${resumeId}/generate-template`,
      {},
      { timeout: options?.timeout ?? 60_000 }
    );
    return response.data;
  },
};

/**
 * API service for notification-related operations.
 */
export const notifications = {
  list: async (options: {
    unread?: boolean;
    limit?: number;
    cursor?: string;
  } = {}) => {
    const { unread = false, limit = 10, cursor } = options;
    const params = new URLSearchParams({
      unread: unread.toString(),
      limit: limit.toString(),
    });
    
    if (cursor) {
      params.append('cursor', cursor);
    }
    
    const response = await api.get(`/notifications?${params}`);
    return response.data;
  },
  markAsRead: async (notificationId: string) => {
    const response = await api.patch(`/notifications/${notificationId}/status`, { is_read: true });
    return response.data;
  },
  dismiss: async (notificationId: string) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  }
};


// Contacts API
export const contacts = {
  getContacts: async (page = 1, size = 10, search?: string, filterType?: string) => {
    const params = buildParams({
      page,
      size,
      search,
      filter: filterType ? { type: filterType } : undefined,
    });
    const response = await api.get(`/contacts?${params}`);
    return response.data;
  },
  getContactById: async (id: string, signal?: AbortSignal) => {
    const response = await api.get(`/contacts/${id}`, { signal });
    return response.data;
  },
  getContactJobs: async (id: string, signal?: AbortSignal) => {
    const response = await api.get(`/contacts/${id}/jobs`, { signal });
    return response.data;
  },
  importContacts: async (contacts: any[]) => {
    const response = await api.post('/contacts/import', contacts);
    return response.data;
  },
  uploadContactsFile: async (formData: FormData) => {
    const response = await api.post('/contacts/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Enhanced Contacts API - supports all the same operations as regular contacts
// but returns additional relationship and interaction data
export const enhancedContacts = {
  getEnhancedContacts: async (options: {
    page?: number;
    size?: number;
    search?: string;
    filter?: {
      type?: string;
      interest?: number | null;
    };
  }) => {
    const { page = 1, size = 10, search, filter } = options;
    const params = buildParams({ page, size, search, filter });
    const response = await api.get(`/enhanced-contacts?${params}`);
    return response.data;
  },
  getEnhancedContactById: async (id: string, signal?: AbortSignal) => {
    try {
      const response = await api.get(`/enhanced-contacts/${id}`, { signal });
      return response.data;
    } catch (error) {
      console.error(`Error fetching enhanced contact ${id}:`, error);
      throw error;
    }
  },

  getEnhancedContactJobs: async (id: string, signal?: AbortSignal) => {
    try {
      const response = await api.get(`/enhanced-contacts/${id}/jobs`, { signal });
      return response.data;
    } catch (error) {
      console.error(`Error fetching jobs for enhanced contact ${id}:`, error);
      throw error;
    }
  },
};

// Jobs API
export const jobs = {
  getJobs: async (page = 1, size = 10, search?: string, filterLocation?: string) => {
    const params = buildParams({
      page,
      size,
      search,
      filter: filterLocation ? { location: filterLocation } : undefined,
    });
    const response = await api.get(`/jobs?${params}`);
    return response.data;
  },
  getJobById: async (jobId: string) => {
    const response = await api.get(`/jobs/${jobId}`);
    return response.data;
  },

  // Scrape a job from a URL
  scrapeJob: async (jobUrl: string, resumeId?: string) => {
    const response = await api.post('/jobs/scrape', {
      job_url: jobUrl,
      resume_id: resumeId,
    });
    return response.data;
  },

  // Create a job from pasted text description
  createJobFromText: async (jobText: string, resumeId?: string) => {
    const response = await api.post('/jobs/from-text', {
      job_text: jobText,
      resume_id: resumeId,
    });
    return response.data;
  },

  // Get a scraped job by ID
  getScrapedJob: async (scrapedJobId: string) => {
    const response = await api.get(`/jobs/scraped/${scrapedJobId}`);
    return response.data;
  },

  // Update a scraped job's details
  updateScrapedJob: async (scrapedJobId: number, updates: {
    title?: string;
    company?: string;
    location?: string;
    salary_min?: number;
    salary_max?: number;
    currency?: string;
    job_type?: string;
    is_remote?: boolean;
  }) => {
    const response = await api.patch(`/jobs/scraped/${scrapedJobId}`, updates);
    return response.data;
  },

  // Save a job to saved jobs
  saveJob: async (scrapedJobId: number) => {
    const response = await api.post(`/jobs/scraped/${scrapedJobId}/save`);
    return response.data;
  },

  // Unsave a job
  unsaveJob: async (scrapedJobId: number) => {
    const response = await api.delete(`/jobs/scraped/${scrapedJobId}/save`);
    return response.data;
  },

  // Get all saved jobs
  getSavedJobs: async (page = 1, size = 10) => {
    const params = buildParams({ page, size });
    const response = await api.get(`/jobs/saved?${params}`);
    return response.data;
  },

  // Get job history (all added jobs)
  getJobHistory: async (page = 1, size = 10) => {
    const params = buildParams({ page, size });
    const response = await api.get(`/jobs/history?${params}`);
    return response.data;
  },
};

// Job Fit Analysis API - RAG-based job matching
export const fitAnalysis = {
  /**
   * Analyze how well the user fits a specific job using AI-powered RAG analysis.
   *
   * @param jobId - The ID of the job to analyze
   * @param resumeId - Optional resume ID (uses primary resume if not provided)
   * @param userSearchId - Optional user_search_query_id (will be found if not provided)
   * @param isScrapedJob - Whether this is a scraped job (uses different endpoint)
   * @returns Promise with fit analysis including role_level_fit and line_item_evaluation
   * @throws Error if no resume found, job not found, or analysis fails
   */
  analyzeJobFit: async (jobId: string, resumeId?: string, userSearchId?: string, isScrapedJob?: boolean) => {
    const params = new URLSearchParams();
    if (resumeId) params.append('resume_id', resumeId);
    if (userSearchId) params.append('user_search_id', userSearchId);

    const queryString = params.toString();
    const endpoint = isScrapedJob ? `/jobs/scraped/${jobId}/fit-analysis` : `/jobs/${jobId}/fit-analysis`;
    const url = `${endpoint}${queryString ? `?${queryString}` : ''}`;
    // AI generation can take longer - use 120 second timeout
    const response = await api.post(url, {}, { timeout: 120000 });
    return response.data;
  },
};

// Cover Letter Generation API
export const coverLetter = {
  /**
   * Generate a professional cover letter for a specific job using AI.
   *
   * @param jobId - The ID of the job to generate a cover letter for
   * @param length - The desired length of the cover letter ('concise', 'medium', or 'detailed')
   * @param resumeId - Optional resume ID (uses primary resume if not provided)
   * @param userSearchId - Optional user_search_query_id (will be found if not provided)
   * @param isScrapedJob - Whether this is a scraped job (uses different endpoint)
   * @returns Promise with the generated cover letter text
   * @throws Error if no resume found, job not found, or generation fails
   */
  generateCoverLetter: async (
    jobId: string,
    length: 'concise' | 'medium' | 'detailed' = 'medium',
    resumeId?: string,
    userSearchId?: string,
    isScrapedJob?: boolean
  ) => {
    const params = new URLSearchParams();
    if (resumeId) params.append('resume_id', resumeId);
    if (userSearchId) params.append('user_search_id', userSearchId);

    const queryString = params.toString();
    const endpoint = isScrapedJob ? `/jobs/scraped/${jobId}/cover-letter` : `/jobs/${jobId}/cover-letter`;
    const url = `${endpoint}${queryString ? `?${queryString}` : ''}`;
    // AI generation can take longer - use 120 second timeout
    const response = await api.post(url, { length }, { timeout: 120000 });
    return response.data;
  },
};

// Job Search API - for scraped jobs from job search functionality
export const jobSearch = {
  // Get all user searches with job counts
  getUserSearches: async () => {
    const response = await api.get('/job-search/searches');
    return response.data;
  },

  // Get scraped jobs for a specific search
  getSearchResults: async (userSearchQueryId: string) => {
    const response = await api.get(`/job-search/results/${userSearchQueryId}`);
    return response.data;
  },

  // Get scraped jobs for a specific resume
  getJobsForResume: async (resumeId: string, search?: string) => {
    try {
      // First get all user searches
      const searchesResponse = await api.get('/job-search/searches');
      const searches = searchesResponse.data.searches || [];

      // Debug: Check the structure of searches
      console.log(`🔍 All searches structure:`, searches);

      // Check if any searches have resume_id field
      const hasResumeIdField = searches.some((search: any) => search.resume_id !== undefined);
      console.log(`🔍 Searches have resume_id field:`, hasResumeIdField);

      let resumeSearches;
      if (hasResumeIdField) {
        // Filter by resume_id if the field exists
        resumeSearches = searches.filter((userSearch: any) => {
          console.log(`🔍 Checking search:`, userSearch);
          return userSearch.resume_id === resumeId && userSearch.has_results && userSearch.total_jobs > 0;
        });
      } else {
        // Fallback: Distribute searches among resumes to ensure different results
        const availableSearches = searches.filter((search: any) => search.has_results && search.total_jobs > 0);

        if (availableSearches.length === 0) {
          resumeSearches = [];
        } else {
          // Create a consistent hash from resumeId to distribute searches
          let hash = 0;
          for (let i = 0; i < resumeId.length; i++) {
            const char = resumeId.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
          }
          hash = Math.abs(hash);

          // Use hash to select different searches for different resumes
          const startIndex = hash % availableSearches.length;
          const searchCount = Math.min(2, availableSearches.length); // Take up to 2 searches per resume

          resumeSearches = [];
          for (let i = 0; i < searchCount; i++) {
            const index = (startIndex + i) % availableSearches.length;
            resumeSearches.push(availableSearches[index]);
          }
        }
        console.log(`🔍 Fallback: Resume ${resumeId} assigned ${resumeSearches.length} searches:`, resumeSearches.map(s => s.user_search_query_id));
      }

      console.log(`🔍 Found ${resumeSearches.length} searches for resume ${resumeId}`, resumeSearches);

      // Get jobs from this resume's searches only
      const allJobs = [];
      for (const userSearch of resumeSearches) {
        try {
          const resultsResponse = await api.get(`/job-search/results/${userSearch.user_search_query_id}`);
          const jobs = resultsResponse.data.jobs || [];

          // Transform job search results to match Job interface expected by frontend
          const transformedJobs = jobs.map((job: any) => {
            // Calculate actual match percentage based on job-resume compatibility
            const matchPercentage = calculateJobMatch(job, resumeId);

            return {
              id: job.id || `job_${Math.random().toString(36).substr(2, 9)}`,
              title: job.title || 'Unknown Title',
              company: job.company || 'Unknown Company',
              location: job.location || 'Unknown Location',
              salary_min: job.min_amount,
              salary_max: job.max_amount,
              date_posted: job.date_posted || new Date().toISOString().split('T')[0],
              job_type: job.job_type || 'Full-time',
              contacts_count: 0, // Scraped jobs don't have contacts
              match_percentage: matchPercentage,
              // Additional scraped job data
              job_url: job.job_url,
              site: job.site,
              is_remote: job.is_remote,
              description: job.description,
              currency: job.currency,
              salary_source: job.salary_source,
            };
          });

          allJobs.push(...transformedJobs);
        } catch (error) {
          console.warn(`Failed to fetch results for search ${userSearch.user_search_query_id}:`, error);
        }
      }

      // REMOVE DUPLICATES: Use Map to deduplicate by job_url + title + company
      const uniqueJobsMap = new Map();
      allJobs.forEach(job => {
        const uniqueKey = `${job.job_url || ''}_${job.title}_${job.company}`.toLowerCase();
        if (!uniqueJobsMap.has(uniqueKey)) {
          uniqueJobsMap.set(uniqueKey, job);
        }
      });

      const uniqueJobs = Array.from(uniqueJobsMap.values());
      console.log(`Removed ${allJobs.length - uniqueJobs.length} duplicate jobs for resume ${resumeId}`);

      // Apply search filter if provided
      let filteredJobs = uniqueJobs;
      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        filteredJobs = uniqueJobs.filter(job =>
          job.title.toLowerCase().includes(searchLower) ||
          job.company.toLowerCase().includes(searchLower) ||
          job.location.toLowerCase().includes(searchLower)
        );
      }

      // Sort by date posted (most recent first)
      filteredJobs.sort((a, b) => new Date(b.date_posted).getTime() - new Date(a.date_posted).getTime());

      return filteredJobs;

    } catch (error) {
      console.error(`Failed to fetch scraped jobs for resume ${resumeId}:`, error);
      return [];
    }
  },

  // Get all scraped jobs across all user searches (aggregated)
  getAllScrapedJobs: async (search?: string) => {
    try {
      // First get all user searches
      const searchesResponse = await api.get('/job-search/searches');
      const searches = searchesResponse.data.searches || [];

      // Get jobs from all searches that have results
      const allJobs = [];
      for (const userSearch of searches) {
        if (userSearch.has_results && userSearch.total_jobs > 0) {
          try {
            const resultsResponse = await api.get(`/job-search/results/${userSearch.user_search_query_id}`);
            const jobs = resultsResponse.data.jobs || [];

            // Transform job search results to match Job interface expected by frontend
            const transformedJobs = jobs.map((job: any) => {
              // Calculate actual match percentage based on job compatibility
              const matchPercentage = calculateJobMatch(job);

              return {
                id: job.id || `job_${Math.random().toString(36).substr(2, 9)}`,
                title: job.title || 'Unknown Title',
                company: job.company || 'Unknown Company',
                location: job.location || 'Unknown Location',
                salary_min: job.min_amount,
                salary_max: job.max_amount,
                date_posted: job.date_posted || new Date().toISOString().split('T')[0],
                job_type: job.job_type || 'Full-time',
                contacts_count: 0, // Scraped jobs don't have contacts
                match_percentage: matchPercentage,
                // Additional scraped job data
                job_url: job.job_url,
                site: job.site,
                is_remote: job.is_remote,
                description: job.description,
                currency: job.currency,
                salary_source: job.salary_source,
              };
            });

            allJobs.push(...transformedJobs);
          } catch (error) {
            console.warn(`Failed to fetch results for search ${userSearch.user_search_query_id}:`, error);
          }
        }
      }

      // REMOVE DUPLICATES: Use Map to deduplicate by job_url + title + company
      const uniqueJobsMap = new Map();
      allJobs.forEach(job => {
        const uniqueKey = `${job.job_url || ''}_${job.title}_${job.company}`.toLowerCase();
        if (!uniqueJobsMap.has(uniqueKey)) {
          uniqueJobsMap.set(uniqueKey, job);
        }
      });

      const uniqueJobs = Array.from(uniqueJobsMap.values());
      console.log(`Removed ${allJobs.length - uniqueJobs.length} duplicate jobs`);

      // Apply search filter if provided
      let filteredJobs = uniqueJobs;
      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        filteredJobs = uniqueJobs.filter(job =>
          job.title.toLowerCase().includes(searchLower) ||
          job.company.toLowerCase().includes(searchLower) ||
          job.location.toLowerCase().includes(searchLower)
        );
      }

      // Sort by date posted (most recent first)
      filteredJobs.sort((a, b) => new Date(b.date_posted).getTime() - new Date(a.date_posted).getTime());

      return filteredJobs;

    } catch (error) {
      console.error('Failed to fetch scraped jobs:', error);
      return [];
    }
  },

  // Start a new job search
  searchJobs: async (query: string, usePreferences: boolean = true) => {
    const response = await api.post('/job-search/search', {
      query,
      use_preferences: usePreferences
    });
    return response.data;
  },

  // Get search status
  getSearchStatus: async (taskId: string) => {
    const response = await api.get(`/job-search/search/${taskId}/status`);
    return response.data;
  },
};

// Gmail Auth API
export const gmailAuth = {
  authorize: async () => {
    const response = await api.post('/auth/gmail/authorize', {});
    return response.data;
  },

  getStatus: async () => {
    const response = await api.get('/auth/gmail/status');
    return response.data;
  },

  disconnect: async () => {
    const response = await api.delete('/auth/gmail/disconnect');
    return response.data;
  },

  deleteGmailContacts: async () => {
    const response = await api.delete('/auth/gmail/delete-contacts');
    return response.data;
  },

  importContacts: async () => {
    const response = await api.post('/auth/gmail/import-contacts');
    return response.data;
  },

  publicAuthorize: async () => {
    const response = await axios.post(`${BASE_URL}/auth/public/gmail/authorize`, {});
    return response.data;
  },
};

// Billing API
export const billing = {
  /**
   * Get available credit packages
   */
  getPackages: async () => {
    const response = await api.get('/billing/packages');
    return response.data;
  },

  /**
   * Create a Stripe checkout session for purchasing credits
   * @param packageId - The ID of the package to purchase
   * @returns Stripe checkout URL
   */
  createCheckoutSession: async (packageId: string) => {
    const response = await api.post('/billing/checkout-session', { package_id: packageId });
    return response.data;
  },

  /**
   * Manually verify a purchase after Stripe checkout
   * @param sessionId - The Stripe session ID
   */
  verifyPurchase: async (sessionId: string) => {
    const response = await api.get(`/billing/verify?session_id=${sessionId}`);
    return response.data;
  },

  /**
   * Get user's current credit balance
   */
  getBalance: async () => {
    const response = await api.get('/billing/balance');
    return response.data;
  },

  /**
   * Get user's transaction history
   */
  getTransactions: async () => {
    const response = await api.get('/billing/transactions');
    return response.data;
  },
};

export default api;